import { z } from "zod";

/**
 * The one place that reads `import.meta.env`. Everything else imports `env`
 * from here instead of touching `import.meta.env` directly, so there is a
 * single, typed, validated surface for configuration — and a misconfigured
 * deployment fails immediately at startup with a clear message instead of
 * surfacing as a confusing runtime error deep inside a network call
 * (docs/architecture/environment.md — "Fail fast if a required variable is
 * missing").
 *
 * Only `VITE_API_URL` is required today because it's the only one anything
 * in the bootstrap actually reads. The rest are declared per
 * docs/architecture/environment.md's client section, but stay optional until
 * the feature that needs them (maps, Cloudinary uploads, realtime chat)
 * exists — an optional var with nothing reading it yet is honest; a required
 * one would just block every developer's local startup for no reason.
 */
const envSchema = z.object({
  VITE_API_URL: z
    .string()
    .url("VITE_API_URL must be a full URL, e.g. http://localhost:5000/api/v1"),
  VITE_SOCKET_URL: z.string().url().optional(),
  VITE_GOOGLE_MAPS_KEY: z.string().optional(),
  VITE_CLOUDINARY_CLOUD_NAME: z.string().optional(),
});

function loadEnv() {
  const parsed = envSchema.safeParse(import.meta.env);

  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((issue) => `  - ${issue.path.join(".")}: ${issue.message}`)
      .join("\n");

    throw new Error(
      `Invalid environment configuration. Check client/.env against client/.env.example:\n${issues}`,
    );
  }

  return parsed.data;
}

export const env = loadEnv();
